import type { ILog } from '@monofe/util';
import consoleLoggerInstantiator from './log.console';
import type { ILogger } from './log.if';
import { LOG_LEVEL } from './log.if';

/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/dot-notation */
const BUILD_ENVIRONMENT = String(process.env['NODE_ENV']) || 'unknown';

const ENV_IS_DEVELOPMENT = BUILD_ENVIRONMENT !== 'production';

const LOG_LEVEL_DEV = [
    LOG_LEVEL.MILESTONE,
    LOG_LEVEL.TRACE,
    LOG_LEVEL.VERBOSE,
    LOG_LEVEL.INFO,
    LOG_LEVEL.WARN,
    LOG_LEVEL.ERROR,
    LOG_LEVEL.FATAL,
    LOG_LEVEL.ASSERT,
];
const LOG_LEVEL_PROD = [LOG_LEVEL.WARN, LOG_LEVEL.ERROR, LOG_LEVEL.FATAL, LOG_LEVEL.ASSERT];

const _logLevels = (): LOG_LEVEL[] => (ENV_IS_DEVELOPMENT ? LOG_LEVEL_DEV : LOG_LEVEL_PROD);

const _buildLoggers = (logLevels: LOG_LEVEL[]): ILogger[] => [consoleLoggerInstantiator(logLevels)];

function logger(): ILog {
    const logLevels = _logLevels();
    const loggers: ILogger[] = _buildLoggers(logLevels);
    const middlewares: ((message: string) => string)[] = [];

    const isLogLevelEnabled = (logLevel: LOG_LEVEL): boolean => logLevels.includes(logLevel);

    const logAll = (logLevel: LOG_LEVEL, message: string): void => {
        const logMessage =
            middlewares.length > 0
                ? middlewares.reduce(
                      (currentMessage, middleware) => middleware(currentMessage),
                      message,
                  )
                : message;

        if (logMessage.length <= 0) {
            /* Nothing to do */
            return;
        }

        for (const logger of loggers) {
            logger.log(logLevel, message);
        }
    };

    const assertAll = (condition: boolean | number | string, message: string): void => {
        for (const logger of loggers) {
            logger.assert(condition, message);
        }
    };

    const logNoop = (_: string): void => {
        /* No-Op */
    };

    const logAssertNoop = (_: boolean | number | string, __: string | undefined): void => {
        /* No-Op */
    };

    const module: ILog = {
        milestone: isLogLevelEnabled(LOG_LEVEL.MILESTONE)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.MILESTONE, msg);
              }
            : logNoop,
        verbose: isLogLevelEnabled(LOG_LEVEL.VERBOSE)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.VERBOSE, msg);
              }
            : logNoop,
        trace: isLogLevelEnabled(LOG_LEVEL.TRACE)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.TRACE, msg);
              }
            : logNoop,
        info: isLogLevelEnabled(LOG_LEVEL.INFO)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.INFO, msg);
              }
            : logNoop,
        warn: isLogLevelEnabled(LOG_LEVEL.WARN)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.WARN, msg);
              }
            : logNoop,
        warning: isLogLevelEnabled(LOG_LEVEL.WARN)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.WARN, msg);
              }
            : logNoop,
        error: isLogLevelEnabled(LOG_LEVEL.ERROR)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.ERROR, msg);
              }
            : logNoop,
        fatal: isLogLevelEnabled(LOG_LEVEL.FATAL)
            ? (msg: string): void => {
                  logAll(LOG_LEVEL.FATAL, msg);
              }
            : logNoop,
        assert: isLogLevelEnabled(LOG_LEVEL.ASSERT)
            ? (condition: boolean | number | string, message: string | undefined): void => {
                  assertAll(condition, message ?? 'Assertion check failed');
              }
            : logAssertNoop,

        addMiddleware: middleware => middlewares.push(middleware),
        removeMiddleware: middleware => delete middlewares[middlewares.indexOf(middleware)],
    };

    return module;
}

export default logger;
